import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Card,
  CardMedia,
  Chip,
  Switch,
  FormControlLabel,
  Skeleton,
  Button,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { es, ca } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { notification as notificationApi } from '../../utils/api';
import { toast } from 'react-toastify';

const ITEMS_PER_PAGE = 20;

const NotificationsPage = () => {
  const { t, i18n } = useTranslation(['notifications', 'common', 'workshop']);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchNotifications = async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const response = await notificationApi.getAll({
        limit: ITEMS_PER_PAGE,
        skip: (pageNum - 1) * ITEMS_PER_PAGE
      });

      console.log('Raw notifications from API:', response.notifications);
      const newNotifications = filterValidNotifications(response.notifications);
      console.log('Filtered notifications:', newNotifications);
      console.log('Gallery notifications:', newNotifications.filter(n => n.type === 'gallery'));
      
      setNotifications(prev => append ? [...prev, ...newNotifications] : newNotifications);
      setHasMore(newNotifications.length === ITEMS_PER_PAGE);
      setPage(pageNum);
      setError(null);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError(t('common.error.loading'));
      toast.error(t('common.error.loading'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchNotifications(page + 1, true);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notification =>
          notification._id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
      toast.success(t('notifications.markedAsRead'));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error(t('common.error.action'));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );
      toast.success(t('notifications.allMarkedAsRead'));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error(t('common.error.action'));
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read when clicked
      if (!notification.read) {
        await handleMarkAsRead(notification._id);
      }

      // Navigate based on notification type
      switch (notification.type) {
        case 'task':
          if (notification.task?._id) {
            try {
              // First check if the task exists
              const response = await fetch(`/api/tasks/${notification.task._id}`);
              if (!response.ok) {
                if (response.status === 404) {
                  // Task was deleted, remove the notification from the list
                  setNotifications(prev => prev.filter(n => n._id !== notification._id));
                  toast.error(t('notifications.error.taskDeleted'));
                  return;
                }
                throw new Error('Failed to fetch task');
              }
              navigate(`/workshop/tasks/${notification.task._id}`);
            } catch (error) {
              console.error('Error checking task existence:', error);
              toast.error(t('notifications.error.taskNotFound'));
            }
          } else {
            toast.error(t('notifications.error.invalidTask'));
          }
          break;
        case 'event':
          if (notification.event?._id) {
            try {
              // First check if the event exists
              const response = await fetch(`/api/events/${notification.event._id}`);
              if (!response.ok) {
                if (response.status === 404) {
                  // Event was deleted, remove the notification from the list
                  setNotifications(prev => prev.filter(n => n._id !== notification._id));
                  toast.error(t('notifications.error.eventDeleted'));
                  return;
                }
                throw new Error('Failed to fetch event');
              }
              navigate(`/events/${notification.event._id}`);
            } catch (error) {
              console.error('Error checking event existence:', error);
              toast.error(t('notifications.error.eventNotFound'));
            }
          } else {
            toast.error(t('notifications.error.invalidEvent'));
          }
          break;
        case 'gallery':
          if (notification.gallery?._id) {
            try {
              // First check if the gallery exists
              const response = await fetch(`/api/galleries/${notification.gallery._id}`);
              if (!response.ok) {
                if (response.status === 404) {
                  // Gallery was deleted, remove the notification from the list
                  setNotifications(prev => prev.filter(n => n._id !== notification._id));
                  toast.error(t('notifications.error.galleryDeleted'));
                  return;
                }
                throw new Error('Failed to fetch gallery');
              }
              navigate(`/galleries/${notification.gallery._id}`);
            } catch (error) {
              console.error('Error checking gallery existence:', error);
              toast.error(t('notifications.error.galleryNotFound'));
            }
          } else {
            toast.error(t('notifications.error.invalidGallery'));
          }
          break;
        case 'noticeboard':
          if (notification.notice?._id) {
            try {
              // First check if the notice exists
              const response = await fetch(`/api/noticeboard/${notification.notice._id}`);
              if (!response.ok) {
                if (response.status === 404) {
                  // Notice was deleted, remove the notification from the list
                  setNotifications(prev => prev.filter(n => n._id !== notification._id));
                  toast.error(t('notifications.error.noticeDeleted'));
                  return;
                }
                throw new Error('Failed to fetch notice');
              }
              navigate(`/noticeboard`);
            } catch (error) {
              console.error('Error checking notice existence:', error);
              toast.error(t('notifications.error.noticeNotFound'));
            }
          } else {
            toast.error(t('notifications.error.invalidNotice'));
          }
          break;
        default:
          console.warn('Unknown notification type:', notification.type);
          toast.error(t('notifications.error.unknownType'));
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      toast.error(t('common.error.action'));
    }
  };

  const LoadingSkeleton = () => (
    <Box sx={{ width: '100%', mt: 2 }}>
      {[1, 2, 3].map((item) => (
        <Box key={item} sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" height={60} />
          <Box sx={{ pt: 0.5 }}>
            <Skeleton />
            <Skeleton width="60%" />
          </Box>
        </Box>
      ))}
    </Box>
  );

  const getNotificationMessage = (notification) => {
    console.log('Getting message for notification:', notification);
    const getMainMessage = () => {
      const sender = notification.notificationSender?.name || t('common:unknownUser');
      const taskTitle = notification.task?.title || t('common:unknownTask');
      const carName = notification.task?.car?.name || t('common:unknownCar');
      const status = notification.metadata?.taskStatus 
        ? t(`workshop:status.${notification.metadata.taskStatus.toLowerCase()}`)
        : t('workshop:status.pending');
      const eventTitle = notification.event?.title || t('common:unknownEvent');
      const eventLocation = notification.event?.location || t('common:unknownLocation');
      const galleryTitle = notification.gallery?.title || notification.metadata?.galleryTitle || t('common:unknownGallery');
      const noticeTitle = notification.notice?.title || notification.metadata?.noticeTitle || t('common:unknownNotice');

      console.log('Notification type:', notification.type);
      console.log('Notification subtype:', notification.subtype);
      console.log('Gallery data:', notification.gallery);
      console.log('Metadata:', notification.metadata);

      switch (notification.type) {
        case 'task':
          if (notification.subtype === 'task_status_updated') {
            return (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  {sender} {t('notifications:task.statusUpdated')} {taskTitle}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {t('notifications:task.newStatus')}: {status}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {t('notifications:task.forCar')} {carName}
                </Typography>
              </>
            );
          } else if (notification.subtype === 'task_payment_received') {
            const amount = notification.metadata?.amount || 0;
            const laborCost = notification.metadata?.laborCost || 0;
            const totalCost = Number(amount) + Number(laborCost);
            
            return (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  {sender} {t('notifications:task.paymentReceived')} {taskTitle}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {laborCost > 0 ? (
                    <>
                      {t('notifications:task.repairCost')}: {amount}€<br />
                      {t('notifications:task.laborCost')}: {laborCost}€<br />
                      {t('notifications:task.totalCost')}: {totalCost}€
                    </>
                  ) : (
                    <>
                      {t('notifications:task.amount')}: {amount}€
                    </>
                  )}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {t('notifications:task.forCar')} {carName}
                </Typography>
              </>
            );
          } else {
            return (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  {sender} {t('notifications:task.created')} {taskTitle}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {t('notifications:task.forCar')} {carName}
                </Typography>
              </>
            );
          }
        case 'event':
          if (notification.subtype === 'event_created') {
            return (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  {sender} {t('notifications:event.created')} {eventTitle}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {t('notifications:event.location')}: {eventLocation}
                </Typography>
              </>
            );
          } else if (notification.subtype === 'event_updated') {
            return (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  {sender} {t('notifications:event.updated')} {eventTitle}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {t('notifications:event.location')}: {eventLocation}
                </Typography>
              </>
            );
          }
          return null;
        case 'gallery':
          if (notification.subtype === 'gallery_created') {
            return (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  {sender} {t('notifications:gallery.created')} {galleryTitle}
                </Typography>
                {notification.metadata?.eventTitle && (
                  <Typography
                    component="div"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.95rem' },
                      color: 'text.secondary',
                      mb: 0.5
                    }}
                  >
                    {t('notifications:gallery.forEvent')}: {notification.metadata.eventTitle}
                  </Typography>
                )}
              </>
            );
          }
          return null;
        case 'noticeboard':
          if (notification.subtype === 'notice_created') {
            return (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  {sender} {t('notifications:noticeboard.created')} {noticeTitle}
                </Typography>
                {notification.metadata?.noticeContent && (
                  <Typography
                    component="div"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.95rem' },
                      color: 'text.secondary',
                      mb: 0.5,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {convertUrlsToLinks(notification.metadata.noticeContent)}
                  </Typography>
                )}
              </>
            );
          } else if (notification.subtype === 'notice_updated') {
            return (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}
                >
                  {sender} {t('notifications:noticeboard.updated')} {noticeTitle}
                </Typography>
                {notification.metadata?.noticeContent && (
                  <Typography
                    component="div"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.95rem' },
                      color: 'text.secondary',
                      mb: 0.5,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {convertUrlsToLinks(notification.metadata.noticeContent)}
                  </Typography>
                )}
              </>
            );
          }
          return null;
        default:
          return null;
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {getMainMessage()}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem'
          }}
        >
          {formatDistanceToNow(new Date(notification.createdAt), {
            addSuffix: true,
            locale: i18n.language === 'es' ? es : i18n.language === 'ca' ? ca : undefined
          })}
        </Typography>
      </Box>
    );
  };

  const groupNotificationsByDate = (notifications) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    return notifications.reduce((groups, notification) => {
      const notifDate = new Date(notification.createdAt);
      notifDate.setHours(0, 0, 0, 0);

      let group;
      if (notifDate.getTime() === today.getTime()) {
        group = 'today';
      } else if (notifDate.getTime() === yesterday.getTime()) {
        group = 'yesterday';
      } else {
        group = 'earlier';
      }

      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(notification);
      return groups;
    }, {});
  };

  // Add a function to filter out notifications for deleted items
  const filterValidNotifications = (notifications) => {
    console.log('Filtering notifications:', notifications);
    const filtered = notifications.filter(notification => {
      if (notification.type === 'task' && !notification.task?._id) {
        console.log('Filtering out invalid task notification:', notification);
        return false;
      }
      if (notification.type === 'event' && !notification.event?._id) {
        console.log('Filtering out invalid event notification:', notification);
        return false;
      }
      if (notification.type === 'gallery' && !notification.gallery?._id) {
        console.log('Filtering out invalid gallery notification:', notification);
        return false;
      }
      if (notification.type === 'noticeboard' && !notification.notice?._id) {
        console.log('Filtering out invalid noticeboard notification:', notification);
        return false;
      }
      return true;
    });
    console.log('Filtered notifications result:', filtered);
    return filtered;
  };

  // Add the utility function to convert URLs to clickable links
  const convertUrlsToLinks = (text) => {
    if (!text) return '';
    
    // URL regex pattern that matches http, https, ftp URLs
    const urlRegex = /(https?:\/\/|ftp:\/\/|www\.)[^\s\n]+/g;
    
    // Split text by URLs
    const parts = text.split(urlRegex);
    
    // Get all URLs that match the regex
    const urls = text.match(urlRegex) || [];
    
    // If no URLs found, return the original text
    if (urls.length === 0) {
      return text;
    }
    
    // Combine text parts with URL links
    const result = [];
    parts.forEach((part, index) => {
      result.push(part);
      if (index < urls.length) {
        const url = urls[index];
        const href = url.startsWith('www.') ? `https://${url}` : url;
        
        // Create a cleaner display text without protocol
        const displayText = url.replace(/^(https?:\/\/|ftp:\/\/|www\.)/, '');
        
        result.push(
          <Link 
            key={index} 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ wordBreak: 'break-word' }}
          >
            {displayText}
          </Link>
        );
      }
    });
    
    return result;
  };

  if (loading && !loadingMore) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Typography variant="h4" gutterBottom>
            {t('notifications.title')}
          </Typography>
          <LoadingSkeleton />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => fetchNotifications(1)}
            startIcon={<CircularProgress size={20} />}
          >
            {t('common.retry')}
          </Button>
        </Box>
      </Container>
    );
  }

  const groupedNotifications = groupNotificationsByDate(notifications);

  return (
    <Container maxWidth="md">
      <Box py={4}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={notifications.every(n => n.read)}
                onChange={handleMarkAllAsRead}
                color="primary"
              />
            }
            label={t('notifications.markAllAsRead')}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                color: 'text.secondary'
              }
            }}
          />
        </Box>

        {Object.entries(groupedNotifications).map(([group, groupNotifications]) => (
          <Box key={group} mb={3}>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 2, textTransform: 'capitalize' }}
            >
              {t(`notifications.timeGroups.${group}`)}
            </Typography>
            <List>
              {groupNotifications.map((notification, index) => (
                <React.Fragment key={notification._id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      py: 2,
                      opacity: notification.read ? 0.7 : 1,
                      backgroundColor: notification.read ? 'action.hover' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemAvatar sx={{ minWidth: 56 }}>
                      <Avatar
                        src={notification.notificationSender?.avatar || '/default-avatar.png'}
                        alt={notification.notificationSender?.name || t('common.unknownUser')}
                        sx={{ 
                          width: 40, 
                          height: 40
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={getNotificationMessage(notification)}
                      sx={{ 
                        pr: { xs: 1, sm: 2 },
                        my: 0,
                        flex: '1 1 auto'
                      }}
                    />
                    
                    {notification.type !== 'noticeboard' && (
                      <Card 
                        sx={{ 
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="100%"
                          image={
                            notification.type === 'event'
                              ? (notification.event?.image || '/default-event.png')
                              : notification.type === 'gallery'
                              ? (notification.gallery?.image || '/default-gallery.png')
                              : (notification.task?.car?.image || '/default-car.png')
                          }
                          alt={
                            notification.type === 'event'
                              ? (notification.event?.title || t('common.unknownEvent'))
                              : notification.type === 'gallery'
                              ? (notification.gallery?.title || t('common.unknownGallery'))
                              : (notification.task?.car?.name || t('common.unknownCar'))
                          }
                          onError={(e) => {
                            e.target.src = notification.type === 'event' 
                              ? '/default-event.png' 
                              : notification.type === 'gallery'
                              ? '/default-gallery.png'
                              : '/default-car.png';
                          }}
                          sx={{ 
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%'
                          }}
                        />
                      </Card>
                    )}
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        ))}

        {Object.keys(groupedNotifications).length === 0 && (
          <Typography color="text.secondary" align="center" py={4}>
            {t('notifications.empty')}
          </Typography>
        )}

        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={20} /> : null}
            >
              {loadingMore ? t('common.loading') : t('common.loadMore')}
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default NotificationsPage; 